import { useState, useEffect, useMemo } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import {
  Plus,
  Target,
  Plane,
  Home,
  Car,
  GraduationCap,
  Heart,
  Shield,
  TrendingUp,
  Wallet,
  Gift,
  Baby,
  Briefcase,
  X,
  Check,
  ArrowLeft,
  CreditCard,
  Banknote,
  CircleDollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Trash2,
  Calendar,
  Pencil,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, useCurrency } from '@/contexts';
import { useAccounts } from '@/hooks/useAccounts';
import { useTranslation } from '@/hooks/useTranslation';
import { CustomSelect } from '@/components/ui/custom-select';
import { useGoalDeposits } from '@/hooks/useGoalDeposits';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Goal preset types with beautiful gradient backgrounds
// Images are optional - gradient fallback always available
const GOAL_PRESETS = [
  {
    id: 'vacation',
    name: 'Vacation',
    nameFr: 'Vacances',
    icon: Plane,
    color: 'from-pink-400 to-rose-500',
    bgColor:
      'bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop',
    defaultTarget: 5000,
  },
  {
    id: 'emergency',
    name: 'Emergency Fund',
    nameFr: "Fonds d'urgence",
    icon: Shield,
    color: 'from-red-400 to-orange-500',
    bgColor:
      'bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30',
    image: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=600&h=400&fit=crop',
    defaultTarget: 10000,
  },
  {
    id: 'home',
    name: 'Home Down Payment',
    nameFr: 'Apport immobilier',
    icon: Home,
    color: 'from-blue-400 to-indigo-500',
    bgColor:
      'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30',
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&h=400&fit=crop',
    defaultTarget: 50000,
  },
  {
    id: 'car',
    name: 'New Car',
    nameFr: 'Nouvelle voiture',
    icon: Car,
    color: 'from-gray-400 to-slate-600',
    bgColor:
      'bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-900/30 dark:to-slate-900/30',
    image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&h=400&fit=crop',
    defaultTarget: 25000,
  },
  {
    id: 'education',
    name: 'Education',
    nameFr: 'Éducation',
    icon: GraduationCap,
    color: 'from-purple-400 to-violet-500',
    bgColor:
      'bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/30 dark:to-violet-900/30',
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&h=400&fit=crop',
    defaultTarget: 15000,
  },
  {
    id: 'wedding',
    name: 'Wedding',
    nameFr: 'Mariage',
    icon: Heart,
    color: 'from-rose-400 to-pink-500',
    bgColor:
      'bg-gradient-to-br from-rose-100 to-pink-100 dark:from-rose-900/30 dark:to-pink-900/30',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop',
    defaultTarget: 20000,
  },
  {
    id: 'retirement',
    name: 'Retirement',
    nameFr: 'Retraite',
    icon: TrendingUp,
    color: 'from-teal-400 to-emerald-500',
    bgColor:
      'bg-gradient-to-br from-teal-100 to-emerald-100 dark:from-teal-900/30 dark:to-emerald-900/30',
    image: 'https://images.unsplash.com/photo-1501876725168-00c445821c9e?w=600&h=400&fit=crop',
    defaultTarget: 100000,
  },
  {
    id: 'savings',
    name: 'General Savings',
    nameFr: 'Épargne générale',
    icon: Wallet,
    color: 'from-green-400 to-emerald-500',
    bgColor:
      'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&h=400&fit=crop',
    defaultTarget: 5000,
  },
  {
    id: 'baby',
    name: 'Baby Fund',
    nameFr: 'Fonds bébé',
    icon: Baby,
    color: 'from-cyan-400 to-sky-500',
    bgColor: 'bg-gradient-to-br from-cyan-100 to-sky-100 dark:from-cyan-900/30 dark:to-sky-900/30',
    image: 'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=600&h=400&fit=crop',
    defaultTarget: 10000,
  },
  {
    id: 'business',
    name: 'Start a Business',
    nameFr: 'Créer une entreprise',
    icon: Briefcase,
    color: 'from-amber-400 to-yellow-500',
    bgColor:
      'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30',
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&h=400&fit=crop',
    defaultTarget: 30000,
  },
  {
    id: 'gift',
    name: 'Special Gift',
    nameFr: 'Cadeau spécial',
    icon: Gift,
    color: 'from-fuchsia-400 to-purple-500',
    bgColor:
      'bg-gradient-to-br from-fuchsia-100 to-purple-100 dark:from-fuchsia-900/30 dark:to-purple-900/30',
    image: 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=600&h=400&fit=crop',
    defaultTarget: 1000,
  },
  {
    id: 'custom',
    name: 'Custom Goal',
    nameFr: 'Objectif personnalisé',
    icon: Target,
    color: 'from-indigo-400 to-purple-500',
    bgColor:
      'bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30',
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&h=400&fit=crop',
    defaultTarget: 5000,
  },
];

const DEBT_PRESETS = [
  {
    id: 'loan_repayment',
    name: 'Loan Repayment',
    nameFr: 'Remboursement crédit',
    icon: CreditCard,
    color: 'from-red-500 to-rose-600',
    bgColor: 'bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop',
    defaultTarget: 20000,
  },
  {
    id: 'personal_debt',
    name: 'Personal Debt',
    nameFr: 'Dette personnelle',
    icon: Banknote,
    color: 'from-orange-500 to-amber-600',
    bgColor:
      'bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30',
    image: 'https://images.unsplash.com/photo-1607863680198-23d4b2565df0?w=600&h=400&fit=crop',
    defaultTarget: 5000,
  },
  {
    id: 'other_debt',
    name: 'Other Debts',
    nameFr: 'Autres dettes',
    icon: CircleDollarSign,
    color: 'from-slate-500 to-gray-600',
    bgColor:
      'bg-gradient-to-br from-slate-100 to-gray-100 dark:from-slate-900/30 dark:to-gray-900/30',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=400&fit=crop',
    defaultTarget: 10000,
  },
];

const ALL_PRESETS = [...GOAL_PRESETS, ...DEBT_PRESETS];

interface Goal {
  id: string;
  presetId: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  targetDate: string | null;
  linkedAccountId: string | null;
  priority: number;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
}

// ---------- Prediction helper ----------
function computePrediction(
  createdAt: string,
  currentAmount: number,
  targetAmount: number,
  cumulativeData: { date: string; amount: number }[]
) {
  if (currentAmount >= targetAmount) return null;
  if (cumulativeData.length < 2) return null;

  const creationDate = new Date(createdAt);
  const now = new Date();
  const monthsElapsed =
    (now.getFullYear() - creationDate.getFullYear()) * 12 +
      (now.getMonth() - creationDate.getMonth()) || 1;
  const avgPerMonth = currentAmount / monthsElapsed;
  if (avgPerMonth <= 0) return null;

  const remaining = targetAmount - currentAmount;
  const monthsToGoal = remaining / avgPerMonth;
  const targetDate = new Date(now);
  targetDate.setMonth(targetDate.getMonth() + Math.ceil(monthsToGoal));
  return targetDate;
}

export default function GoalsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const { formatAmount } = useCurrency();
  const locale = language;
  const { accounts, activeAccounts, loadAccounts } = useAccounts();
  const { toast } = useToast();

  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPresetSelector, setShowPresetSelector] = useState(false);

  // Detail panel
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Deposit / Withdrawal modal
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositType, setDepositType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositAccount, setDepositAccount] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [depositNote, setDepositNote] = useState('');
  const [depositSaving, setDepositSaving] = useState(false);

  // Edit goal states
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editTarget, setEditTarget] = useState('');
  const [editDate, setEditDate] = useState('');

  // Form states
  const [selectedPreset, setSelectedPreset] = useState<(typeof ALL_PRESETS)[0] | null>(null);
  const [goalName, setGoalName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Deposits hook (for selected goal)
  const {
    deposits,
    loading: depositsLoading,
    addDeposit,
    deleteDeposit,
    deleteAllForGoal,
    currentAmount: depositsCurrentAmount,
    cumulativeData,
  } = useGoalDeposits(selectedGoal?.id);

  // Load goals from user metadata
  useEffect(() => {
    const loadGoals = async () => {
      if (!user) return;

      try {
        const {
          data: { user: userData },
        } = await supabase.auth.getUser();
        const savedGoals = userData?.user_metadata?.goals || [];
        setGoals(savedGoals);
      } catch (error) {
        console.error('Error loading goals:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGoals();
  }, [user]);

  // Save goals to user metadata
  const saveGoals = async (newGoals: Goal[]) => {
    try {
      const { error } = await supabase.auth.updateUser({
        data: { goals: newGoals },
      });

      if (error) throw error;
      setGoals(newGoals);
      window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
      return true;
    } catch (error) {
      console.error('Error saving goals:', error);
      toast({
        title: 'Error',
        description: 'Failed to save goal',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleSelectPreset = (preset: (typeof ALL_PRESETS)[0]) => {
    setSelectedPreset(preset);
    setGoalName(locale === 'fr' ? preset.nameFr : preset.name);
    setTargetAmount(preset.defaultTarget.toString());
    setShowPresetSelector(false);
  };

  const handleCreateGoal = async () => {
    if (!selectedPreset || !goalName || !targetAmount) return;

    setSaving(true);

    const newGoal: Goal = {
      id: crypto.randomUUID(),
      presetId: selectedPreset.id,
      name: goalName,
      targetAmount: parseFloat(targetAmount),
      currentAmount: 0,
      targetDate: targetDate || null,
      linkedAccountId: null,
      priority: goals.length + 1,
      status: 'active',
      createdAt: new Date().toISOString(),
    };

    const success = await saveGoals([...goals, newGoal]);

    if (success) {
      toast({
        title: t('goals.created'),
        description: t('goals.createdDesc'),
      });
      resetForm();
      setShowAddModal(false);
    }

    setSaving(false);
  };

  const handleDeleteGoal = async (goalId: string) => {
    // Delete deposits from DB first
    await deleteAllForGoal(goalId);
    const newGoals = goals.filter((g) => g.id !== goalId);
    const success = await saveGoals(newGoals);

    if (success) {
      toast({
        title: t('goals.deleted'),
        description: t('goals.deletedDesc'),
      });
      setShowDetailModal(false);
      setSelectedGoal(null);
    }
  };

  const handleArchiveGoal = async (goalId: string) => {
    const newGoals = goals.map((g) =>
      g.id === goalId ? { ...g, status: 'archived' as const } : g
    );
    const success = await saveGoals(newGoals);

    if (success) {
      toast({
        title: t('goals.archived'),
        description: t('goals.archivedDesc'),
      });
      setShowDetailModal(false);
      setSelectedGoal(null);
    }
  };

  const handleUnarchiveGoal = async (goalId: string) => {
    const newGoals = goals.map((g) => (g.id === goalId ? { ...g, status: 'active' as const } : g));
    const success = await saveGoals(newGoals);

    if (success) {
      toast({
        title: t('goals.unarchived'),
        description: t('goals.unarchivedDesc'),
      });
    }
  };

  // ---------- Edit Goal ----------
  const startEditing = () => {
    if (!selectedGoal) return;
    setEditName(selectedGoal.name);
    setEditTarget(selectedGoal.targetAmount.toString());
    setEditDate(selectedGoal.targetDate || '');
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedGoal || !editName || !editTarget) return;
    setSaving(true);
    const newGoals = goals.map((g) =>
      g.id === selectedGoal.id
        ? {
            ...g,
            name: editName,
            targetAmount: parseFloat(editTarget),
            targetDate: editDate || null,
          }
        : g
    );
    const success = await saveGoals(newGoals);
    if (success) {
      setSelectedGoal((prev) =>
        prev
          ? {
              ...prev,
              name: editName,
              targetAmount: parseFloat(editTarget),
              targetDate: editDate || null,
            }
          : prev
      );
      setIsEditing(false);
    }
    setSaving(false);
  };

  // ---------- Deposit / Withdrawal ----------
  const openDepositModal = (type: 'deposit' | 'withdrawal') => {
    setDepositType(type);
    setDepositAmount('');
    setDepositAccount('');
    setDepositDate(new Date().toISOString().split('T')[0]);
    setDepositNote('');
    setShowDepositModal(true);
    loadAccounts(); // Refresh accounts list
  };

  const handleAddDeposit = async () => {
    if (!selectedGoal || !depositAmount || parseFloat(depositAmount) <= 0) return;
    setDepositSaving(true);

    const success = await addDeposit({
      goal_id: selectedGoal.id,
      account_id: depositAccount || null,
      type: depositType,
      amount: parseFloat(depositAmount),
      note: depositNote || undefined,
      date: depositDate,
    });

    if (success) {
      // Re-calculate currentAmount from deposits after add
      // We need to wait for refetch, then update metadata
      toast({
        title: depositType === 'deposit' ? t('goals.depositAdded') : t('goals.withdrawalAdded'),
        description:
          depositType === 'deposit' ? t('goals.depositAddedDesc') : t('goals.withdrawalAddedDesc'),
      });
      setShowDepositModal(false);
    }

    setDepositSaving(false);
  };

  // Sync currentAmount in metadata whenever deposits change
  useEffect(() => {
    if (!selectedGoal || depositsLoading) return;
    if (selectedGoal.currentAmount !== depositsCurrentAmount) {
      const newGoals = goals.map((g) =>
        g.id === selectedGoal.id ? { ...g, currentAmount: depositsCurrentAmount } : g
      );
      // Update metadata in background
      supabase.auth.updateUser({ data: { goals: newGoals } }).then(() => {
        setGoals(newGoals);
        setSelectedGoal((prev) =>
          prev && prev.id === selectedGoal.id
            ? { ...prev, currentAmount: depositsCurrentAmount }
            : prev
        );
      });
    }
  }, [depositsCurrentAmount, depositsLoading, selectedGoal?.id]);

  const resetForm = () => {
    setSelectedPreset(null);
    setGoalName('');
    setTargetAmount('');
    setTargetDate('');
    setShowPresetSelector(false);
  };

  const formatCurrency = (amount: number) => formatAmount(amount);

  const getPreset = (presetId: string) => {
    return ALL_PRESETS.find((p) => p.id === presetId) || ALL_PRESETS[ALL_PRESETS.length - 1];
  };

  const activeGoals = goals.filter((g) => g.status === 'active');
  const debtPresetIds = DEBT_PRESETS.map((p) => p.id);
  const activeSavingsGoals = activeGoals.filter((g) => !debtPresetIds.includes(g.presetId));
  const activeDebtGoals = activeGoals.filter((g) => debtPresetIds.includes(g.presetId));
  const archivedGoals = goals.filter((g) => g.status === 'archived');

  // Prediction for detail modal
  const prediction = useMemo(() => {
    if (!selectedGoal) return null;
    return computePrediction(
      selectedGoal.createdAt,
      depositsCurrentAmount,
      selectedGoal.targetAmount,
      cumulativeData
    );
  }, [selectedGoal, depositsCurrentAmount, cumulativeData]);

  // Chart data with prediction line
  const chartData = useMemo(() => {
    if (!selectedGoal || cumulativeData.length === 0) return [];
    const data = cumulativeData.map((d) => ({
      date: new Date(d.date).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
        month: 'short',
        year: '2-digit',
      }),
      amount: d.amount,
    }));
    return data;
  }, [selectedGoal, cumulativeData, locale]);

  // ---------- Goal card renderer ----------
  const renderGoalCard = (goal: Goal) => {
    const preset = getPreset(goal.presetId);
    const progress =
      goal.targetAmount > 0 ? Math.min(100, (goal.currentAmount / goal.targetAmount) * 100) : 0;
    const Icon = preset.icon;

    return (
      <div
        key={goal.id}
        onClick={() => {
          setSelectedGoal(goal);
          setShowDetailModal(true);
        }}
        className={`group relative overflow-hidden rounded-2xl cursor-pointer hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${preset.color}`}
      >
        <div className="absolute inset-0">
          <img
            src={preset.image}
            alt={goal.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <div className="relative p-6 min-h-[200px] flex flex-col justify-end">
          {progress >= 100 && (
            <div className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 bg-green-500/90 rounded-full text-white text-xs font-medium">
              <Check className="w-3 h-3" />
              {t('goals.completed')}
            </div>
          )}
          <div
            className={`w-8 h-8 rounded-lg bg-gradient-to-br ${preset.color} flex items-center justify-center mb-3 shadow-lg`}
          >
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="text-white/70 text-xs font-medium uppercase tracking-wider mb-0.5">
            {locale === 'fr' ? preset.nameFr : preset.name}
          </span>
          <p className="text-white font-semibold text-sm mb-1 truncate">{goal.name}</p>
          <h3 className="text-white text-xl font-bold mb-3">
            {formatCurrency(goal.currentAmount)}
          </h3>
          <div className="w-full">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className="text-white/70">{progress.toFixed(0)}%</span>
              <span className="text-white/70">{formatCurrency(goal.targetAmount)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="goals" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-700/50 sticky top-0 z-40">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('goals.title')}
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                {t('goals.subtitle')}
              </p>
            </div>
            <Button
              id="guide-goals"
              onClick={() => {
                resetForm();
                setShowPresetSelector(true);
                setShowAddModal(true);
              }}
              className="bg-[#16c47f] hover:bg-[#12b070] text-white font-medium scroll-mt-24 target:ring-4 target:ring-[#16c47f]/30 target:ring-offset-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('goals.addGoal')}
            </Button>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeGoals.length === 0 ? (
            /* Empty State */
            <div className="max-w-lg mx-auto mt-20 text-center">
              <div className="w-20 h-20 bg-[#16c47f]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-10 h-10 text-[#16c47f]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {t('goals.emptyTitle')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
                {t('goals.emptyDesc')}
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowPresetSelector(true);
                  setShowAddModal(true);
                }}
                className="bg-[#16c47f] hover:bg-[#12b070] text-white font-medium px-8 py-3 h-auto"
              >
                {t('goals.getStarted')}
              </Button>
            </div>
          ) : (
            /* Goals sections */
            <div className="space-y-8">
              {/* Savings Goals */}
              {activeSavingsGoals.length > 0 && (
                <div>
                  {activeDebtGoals.length > 0 && (
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                      {t('goals.savingsGoals')}
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeSavingsGoals.map(renderGoalCard)}

                    {/* Add Goal Card */}
                    <div
                      onClick={() => {
                        resetForm();
                        setShowPresetSelector(true);
                        setShowAddModal(true);
                      }}
                      className="group relative overflow-hidden rounded-2xl cursor-pointer border-2 border-dashed border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] bg-gray-50 dark:bg-gray-800/50 hover:bg-[#16c47f]/5 transition-all duration-300 min-h-[200px] flex items-center justify-center"
                    >
                      <div className="text-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 group-hover:bg-[#16c47f] flex items-center justify-center mx-auto mb-3 transition-colors">
                          <Plus className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium group-hover:text-[#16c47f] transition-colors">
                          {t('goals.addNew')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Debt Payment Goals */}
              {activeDebtGoals.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-4">
                    {t('goals.debtPayment')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeDebtGoals.map(renderGoalCard)}
                  </div>
                </div>
              )}

              {/* Show Add card alone when no savings goals yet */}
              {activeSavingsGoals.length === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div
                    onClick={() => {
                      resetForm();
                      setShowPresetSelector(true);
                      setShowAddModal(true);
                    }}
                    className="group relative overflow-hidden rounded-2xl cursor-pointer border-2 border-dashed border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] bg-gray-50 dark:bg-gray-800/50 hover:bg-[#16c47f]/5 transition-all duration-300 min-h-[200px] flex items-center justify-center"
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 group-hover:bg-[#16c47f] flex items-center justify-center mx-auto mb-3 transition-colors">
                        <Plus className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium group-hover:text-[#16c47f] transition-colors">
                        {t('goals.addNew')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Archived goals section */}
          {archivedGoals.length > 0 && (
            <div className="mt-12">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('goals.archivedTitle')} ({archivedGoals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedGoals.map((goal) => {
                  const preset = getPreset(goal.presetId);
                  const Icon = preset.icon;

                  return (
                    <div
                      key={goal.id}
                      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${preset.color} flex items-center justify-center`}
                          >
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{goal.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(goal.currentAmount)} /{' '}
                              {formatCurrency(goal.targetAmount)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnarchiveGoal(goal.id)}
                          className="px-3 py-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                        >
                          {t('goals.unarchive')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ========== Add Goal Modal ========== */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedPreset && !showPresetSelector && (
                    <button
                      onClick={() => setShowPresetSelector(true)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-500" />
                    </button>
                  )}
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {showPresetSelector ? t('goals.chooseType') : t('goals.customizeGoal')}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6">
                {showPresetSelector ? (
                  /* Preset Selector Grid */
                  <div className="space-y-6">
                    {/* Savings Goals Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                        {t('goals.savingsGoals')}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {GOAL_PRESETS.map((preset) => {
                          const Icon = preset.icon;
                          return (
                            <button
                              key={preset.id}
                              onClick={() => handleSelectPreset(preset)}
                              className={`group relative overflow-hidden rounded-xl aspect-square cursor-pointer hover:ring-2 hover:ring-[#16c47f] transition-all duration-300 bg-gradient-to-br ${preset.color}`}
                            >
                              <img
                                src={preset.image}
                                alt={locale === 'fr' ? preset.nameFr : preset.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <div
                                  className={`w-8 h-8 rounded-lg bg-gradient-to-br ${preset.color} flex items-center justify-center mb-2 shadow-lg`}
                                >
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-white font-semibold text-sm">
                                  {locale === 'fr' ? preset.nameFr : preset.name}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Debt Payment Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-3">
                        {t('goals.debtPayment')}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {DEBT_PRESETS.map((preset) => {
                          const Icon = preset.icon;
                          return (
                            <button
                              key={preset.id}
                              onClick={() => handleSelectPreset(preset)}
                              className={`group relative overflow-hidden rounded-xl aspect-square cursor-pointer hover:ring-2 hover:ring-red-400 transition-all duration-300 bg-gradient-to-br ${preset.color}`}
                            >
                              <img
                                src={preset.image}
                                alt={locale === 'fr' ? preset.nameFr : preset.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <div
                                  className={`w-8 h-8 rounded-lg bg-gradient-to-br ${preset.color} flex items-center justify-center mb-2 shadow-lg`}
                                >
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-white font-semibold text-sm">
                                  {locale === 'fr' ? preset.nameFr : preset.name}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Goal Customization Form — simplified: name, target, optional date */
                  <div className="space-y-6">
                    {/* Preview */}
                    {selectedPreset && (
                      <div
                        className={`relative overflow-hidden rounded-xl h-40 bg-gradient-to-br ${selectedPreset.color}`}
                      >
                        <img
                          src={selectedPreset.image}
                          alt={goalName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <div
                            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedPreset.color} flex items-center justify-center mb-2 shadow-lg`}
                          >
                            <selectedPreset.icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-white/70 text-xs uppercase tracking-wider">
                            {locale === 'fr' ? selectedPreset.nameFr : selectedPreset.name}
                          </p>
                          <p className="text-white font-bold text-lg">
                            {goalName || t('goals.untitled')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Form Fields */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-700 dark:text-gray-300">
                          {t('goals.name')}
                        </Label>
                        <Input
                          value={goalName}
                          onChange={(e) => setGoalName(e.target.value)}
                          placeholder={t('goals.namePlaceholder')}
                          className="mt-1 h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                        />
                      </div>

                      <div>
                        <Label className="text-gray-700 dark:text-gray-300">
                          {t('goals.targetAmount')}
                        </Label>
                        <Input
                          type="number"
                          value={targetAmount}
                          onChange={(e) => setTargetAmount(e.target.value)}
                          placeholder="10000"
                          className="mt-1 h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                        />
                      </div>

                      <div>
                        <Label className="text-gray-700 dark:text-gray-300">
                          {t('goals.targetDate')}
                        </Label>
                        <div className="mt-1">
                          <DatePicker
                            selected={targetDate ? new Date(targetDate + 'T00:00:00') : null}
                            onChange={(date) =>
                              setTargetDate(date ? date.toISOString().split('T')[0] : '')
                            }
                            dateFormat="dd/MM/yyyy"
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                            wrapperClassName="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAddModal(false);
                          resetForm();
                        }}
                        className="flex-1 h-12 border-gray-200 dark:border-gray-600"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        onClick={handleCreateGoal}
                        disabled={!goalName || !targetAmount || saving}
                        className="flex-1 h-12 bg-[#16c47f] hover:bg-[#12b070] text-white"
                      >
                        {saving ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          t('goals.create')
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== Goal Detail Modal (Enhanced) ========== */}
        {showDetailModal && selectedGoal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              {(() => {
                const preset = getPreset(selectedGoal.presetId);
                const progress =
                  selectedGoal.targetAmount > 0
                    ? Math.min(100, (depositsCurrentAmount / selectedGoal.targetAmount) * 100)
                    : 0;
                const remaining = Math.max(0, selectedGoal.targetAmount - depositsCurrentAmount);
                const Icon = preset.icon;

                return (
                  <>
                    {/* Header with image */}
                    <div className={`relative h-48 bg-gradient-to-br ${preset.color}`}>
                      <img
                        src={preset.image}
                        alt={selectedGoal.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                      <div className="absolute top-4 right-4 flex items-center gap-2">
                        <button
                          onClick={startEditing}
                          className="w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center transition-colors"
                        >
                          <Pencil className="w-4 h-4 text-white" />
                        </button>
                        <button
                          onClick={() => {
                            setShowDetailModal(false);
                            setSelectedGoal(null);
                            setIsEditing(false);
                          }}
                          className="w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${preset.color} flex items-center justify-center mb-2 shadow-lg`}
                        >
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-white/70 text-xs uppercase tracking-wider">
                          {locale === 'fr' ? preset.nameFr : preset.name}
                        </p>
                        <p className="text-white font-bold text-2xl">{selectedGoal.name}</p>
                      </div>
                    </div>

                    {/* Edit form */}
                    {isEditing && (
                      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
                        <div>
                          <Label className="text-gray-700 dark:text-gray-300 text-xs">
                            {t('goals.name')}
                          </Label>
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="mt-1 h-10 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700 dark:text-gray-300 text-xs">
                            {t('goals.targetAmount')}
                          </Label>
                          <Input
                            type="number"
                            value={editTarget}
                            onChange={(e) => setEditTarget(e.target.value)}
                            className="mt-1 h-10 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700 dark:text-gray-300 text-xs">
                            {t('goals.targetDate')}
                          </Label>
                          <div className="mt-1">
                            <DatePicker
                              selected={editDate ? new Date(editDate + 'T00:00:00') : null}
                              onChange={(date) =>
                                setEditDate(date ? date.toISOString().split('T')[0] : '')
                              }
                              dateFormat="dd/MM/yyyy"
                              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                              wrapperClassName="w-full"
                              isClearable
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button
                            variant="outline"
                            onClick={() => setIsEditing(false)}
                            className="flex-1 h-9 text-sm border-gray-200 dark:border-gray-600"
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            onClick={handleSaveEdit}
                            disabled={!editName || !editTarget || saving}
                            className="flex-1 h-9 text-sm bg-[#16c47f] hover:bg-[#12b070] text-white"
                          >
                            {saving ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              t('common.save') || 'Save'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Stats grid */}
                    <div className="px-6 py-4 grid grid-cols-2 gap-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('goals.target')}
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(selectedGoal.targetAmount)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('goals.saved')}
                        </p>
                        <p className="text-lg font-bold text-[#16c47f]">
                          {formatCurrency(depositsCurrentAmount)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('goals.remaining')}
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(remaining)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('goals.deadline')}
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {selectedGoal.targetDate
                            ? new Date(selectedGoal.targetDate).toLocaleDateString(
                                locale === 'fr' ? 'fr-FR' : 'en-US',
                                { month: 'short', year: 'numeric' }
                              )
                            : t('goals.noDeadline')}
                        </p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('goals.progress')}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {progress.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#16c47f] to-emerald-400 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Prediction */}
                    {(prediction ||
                      (deposits.length < 2 &&
                        depositsCurrentAmount < selectedGoal.targetAmount)) && (
                      <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
                        {prediction ? (
                          <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl px-4 py-3">
                            <Calendar className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                            <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
                              {t('goals.predictionText').replace(
                                '{date}',
                                prediction.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
                                  month: 'long',
                                  year: 'numeric',
                                })
                              )}
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3">
                            <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {t('goals.predictionInsufficient')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cumulative chart */}
                    {chartData.length > 0 && (
                      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('goals.cumulativeProgress')}
                        </p>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                              <defs>
                                <linearGradient id="goalGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#16c47f" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#16c47f" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                className="text-gray-400"
                              />
                              <YAxis hide />
                              <Tooltip
                                formatter={(value: number) => [
                                  formatCurrency(value),
                                  t('goals.saved'),
                                ]}
                                contentStyle={{
                                  borderRadius: '12px',
                                  border: 'none',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                              />
                              <ReferenceLine
                                y={selectedGoal.targetAmount}
                                stroke="#ef4444"
                                strokeDasharray="4 4"
                                label={{
                                  value: t('goals.target'),
                                  position: 'right',
                                  fontSize: 11,
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="amount"
                                stroke="#16c47f"
                                strokeWidth={2}
                                fill="url(#goalGrad)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Deposit / Withdrawal buttons */}
                    <div className="px-6 py-4 flex gap-3 border-b border-gray-100 dark:border-gray-700">
                      <Button
                        onClick={() => openDepositModal('deposit')}
                        className="flex-1 bg-[#16c47f] hover:bg-[#12b070] text-white"
                      >
                        <ArrowDownToLine className="w-4 h-4 mr-2" />
                        {t('goals.addDeposit')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openDepositModal('withdrawal')}
                        className="flex-1 border-gray-200 dark:border-gray-600"
                      >
                        <ArrowUpFromLine className="w-4 h-4 mr-2" />
                        {t('goals.addWithdrawal')}
                      </Button>
                    </div>

                    {/* Deposit history */}
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {t('goals.depositHistory')}
                      </p>
                      {depositsLoading ? (
                        <div className="flex justify-center py-4">
                          <div className="w-5 h-5 border-2 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : deposits.length === 0 ? (
                        <div className="text-center py-6">
                          <p className="text-sm text-gray-400 dark:text-gray-500">
                            {t('goals.noDeposits')}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {t('goals.noDepositsDesc')}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {[...deposits].reverse().map((dep) => {
                            const acc = accounts.find((a) => a.id === dep.account_id);
                            return (
                              <div
                                key={dep.id}
                                className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      dep.type === 'deposit'
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                                        : 'bg-red-100 dark:bg-red-900/30 text-red-500'
                                    }`}
                                  >
                                    {dep.type === 'deposit' ? (
                                      <ArrowDownToLine className="w-3.5 h-3.5" />
                                    ) : (
                                      <ArrowUpFromLine className="w-3.5 h-3.5" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      {dep.type === 'deposit' ? '+' : '-'}
                                      {formatCurrency(Number(dep.amount))}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      {new Date(dep.date).toLocaleDateString(
                                        locale === 'fr' ? 'fr-FR' : 'en-US'
                                      )}
                                      {acc ? ` · ${acc.name}` : ''}
                                      {dep.note ? ` · ${dep.note}` : ''}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => deleteDeposit(dep.id)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="px-6 py-4 flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => handleArchiveGoal(selectedGoal.id)}
                        className="flex-1 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        {t('goals.archive')}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleDeleteGoal(selectedGoal.id)}
                        className="flex-1"
                      >
                        {t('goals.delete')}
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ========== Deposit / Withdrawal Modal ========== */}
        {showDepositModal && selectedGoal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {depositType === 'deposit' ? t('goals.addDeposit') : t('goals.addWithdrawal')}
                </h2>
                <button
                  onClick={() => setShowDepositModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-xs text-gray-400 dark:text-gray-500 italic -mt-1 mb-1">
                  {language === 'fr'
                    ? 'Votre solde réel reste inchangé. Seule la répartition virtuelle est modifiée.'
                    : 'Your real balance stays unchanged. Only the virtual allocation is updated.'}
                </p>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">{t('goals.amount')}</Label>
                  <Input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="0.00"
                    min="0.01"
                    step="0.01"
                    className="mt-1 h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                    autoFocus
                  />
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">
                    {depositType === 'deposit'
                      ? t('goals.sourceAccount')
                      : t('goals.destinationAccount')}
                  </Label>
                  <CustomSelect
                    value={depositAccount}
                    onChange={(value) => setDepositAccount(value)}
                    placeholder={t('goals.selectAccount')}
                    className="mt-1"
                    options={activeAccounts
                      .filter((a) => a.type === 'asset')
                      .map((account) => ({
                        value: account.id,
                        label: `${account.name} - ${formatCurrency(account.balance)}`,
                      }))}
                  />
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">{t('goals.date')}</Label>
                  <div className="mt-1">
                    <DatePicker
                      selected={depositDate ? new Date(depositDate + 'T00:00:00') : new Date()}
                      onChange={(date) =>
                        setDepositDate(date ? date.toISOString().split('T')[0] : '')
                      }
                      dateFormat="dd/MM/yyyy"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                      wrapperClassName="w-full"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300">
                    {t('goals.noteOptional')}
                  </Label>
                  <Input
                    value={depositNote}
                    onChange={(e) => setDepositNote(e.target.value)}
                    placeholder=""
                    className="mt-1 h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDepositModal(false)}
                    className="flex-1 h-12 border-gray-200 dark:border-gray-600"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleAddDeposit}
                    disabled={!depositAmount || parseFloat(depositAmount) <= 0 || depositSaving}
                    className={`flex-1 h-12 text-white ${
                      depositType === 'deposit'
                        ? 'bg-[#16c47f] hover:bg-[#12b070]'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {depositSaving ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      t('goals.confirm')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
